import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { type ValueFormat, formatValue } from "./valueFormat";

/**
 * {@link formatValue} with the translated absent-value label already supplied.
 *
 * Use this inside components; use `formatValue` directly only where hooks can't run — module-level
 * config, a data hook, or a non-React helper. The difference is solely the `na` sentinel: this
 * resolves `common.na` for the current language, whereas `formatValue` falls back to a literal
 * "N/A". Formatting behaviour is identical, so both stay consistent.
 */
export function useValueFormatter() {
  const { t } = useTranslation();
  const na = t("common.na", { defaultValue: "N/A" });

  const format = useCallback(
    (value: number | null | undefined, valueFormat: ValueFormat) =>
      formatValue(value, { format: valueFormat, na }),
    [na],
  );

  return useMemo(() => ({ format, na }), [format, na]);
}
